import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDrawer } from '@angular/material/sidenav';
import { Address, PdqUser, ValidationResponse } from '@pdqdev/pdq-shared/dist/entities';
import { Subject } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { UpdateUserPayload, UserManagementService } from '../../../../services/user-management/user-management.service';
import { PopoverService } from '../../../../storybook/services/popover/popover.service';
import { ToastService } from '../../../../storybook/services/toast/toast.service';
enum UserAction {
  EDIT = 'edit',
  REMOVE = 'remove',
}
const ACTION_TO_CONFIG = new Map<UserAction, object>([
  [UserAction.EDIT, { label: 'Edit user', destructive: false }],
  [UserAction.REMOVE, { label: 'Remove user', destructive: true }],
]);

enum UserUpdateToastMessage {
  SUCCESS = 'User details updated',
  ERROR = 'Failed to update user details',
}
@Component({
  selector: 'user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent implements OnInit, OnChanges {
  @Input() drawer: MatDrawer;
  @Input() shopLocations: Address[];
  @Input() shopSupportsLocations: boolean;
  @ViewChild('popoverContent') popoverContent;

  user: PdqUser;
  userForm: UntypedFormGroup;
  userLocations: Address[];
  saveIsLoading;
  isEdit: boolean = false;
  responseErrors: string[];
  userActions = [
    {
      id: UserAction.EDIT,
      ...ACTION_TO_CONFIG.get(UserAction.EDIT),
    },
    // {
    //   id: UserAction.REMOVE,
    //   ...ACTION_TO_CONFIG.get(UserAction.REMOVE),
    // },
  ];
  private destroy$ = new Subject();
  constructor(
    private toastService: ToastService,
    private popoverService: PopoverService,
    private userManagement: UserManagementService,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shopLocations) {
      this.initForm(this.user);
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  setEdit(newEdit: boolean) {
    this.isEdit = newEdit;
  }
  initForm(user: PdqUser) {
    this.user = user;
    this.responseErrors = [];
    const locations = user?.locations ? user.locations : this.shopLocations;
    const locationIds = locations?.map((location) => +location.locationId);
    this.userForm = new UntypedFormGroup({
      name: new UntypedFormControl(user?.name ?? '', [Validators.required]),
      phone: new UntypedFormControl(user?.phoneNumber ?? ''),
      title: new UntypedFormControl(user?.title ?? ''),
      locationIds: new UntypedFormControl(locationIds),
    });
  }
  initSubscriptions() {
    this.userManagement.selectedUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      this.userLocations = user?.locations;
      this.initForm(user);
    });
    if (this.drawer) {
      this.drawer._closedStream.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isEdit = false;
      });
    }
  }
  handleDiscard() {
    this.initForm(this.user);
    this.setEdit(false);
  }
  handleSave() {
    this.saveIsLoading = true;
    const latestUserSnapshot = this.userManagement.currentUser;
    const payload = this.prepareUserUpdatePayload();
    this.userManagement
      .updateUser(latestUserSnapshot.id, payload)
      .pipe(
        first(),
        finalize(() => {
          this.saveIsLoading = false;
        }),
      )
      .subscribe(
        (updatedUser: PdqUser) => {
          this.toastService.showToast({ message: UserUpdateToastMessage.SUCCESS, mode: 'success' });
          this.responseErrors = [];
          this.userManagement.currentUser = updatedUser;
          this.setEdit(false);
        },
        (err) => {
          console.log(err);
          this.toastService.showToast({ message: UserUpdateToastMessage.ERROR, mode: 'danger' });
          this.parseValidationResult(err?.error);
        },
      );
  }
  locationSelectHandler(option) {
    const locationId = option.value;
    if (locationId !== this.userForm.value.locationId) {
      this.userForm.controls.locationId.setValue(locationId);
      this.userForm.controls.locationId.markAsDirty();
    }
  }

  locationsSelectHandler(option) {
    const locationIds = this.userForm.value.locationIds ?? [];
    const locationIdControl = this.userForm.controls.locationIds;
    let madeChanges = false;
    const existingIdIdx = locationIds.findIndex((locationId) => +locationId === +option.value);
    const existingId = existingIdIdx !== -1;
    if (option.checked && !existingId) {
      madeChanges = true;
      locationIds.push(option.value);
    } else if (!option.checked && existingId) {
      madeChanges = true;
      locationIds.splice(existingIdIdx, 1);
    }
    locationIdControl.patchValue(locationIds);
    if (madeChanges) {
      locationIdControl.markAsDirty();
    }
  }
  private parseValidationResult(result: ValidationResponse) {
    const errors = [];
    result?.errors.forEach((error) => {
      errors.push(error.message);
    });
    this.responseErrors = errors;
  }
  private prepareUserUpdatePayload() {
    const res: UpdateUserPayload = {};
    const formControls = this.userForm.controls;
    const formValue = this.userForm.value;
    const locations = formValue.locationIds
      ? this.shopLocations.filter((shopLocation) =>
          formValue.locationIds.some((locationId) => +locationId === +shopLocation.locationId),
        )
      : undefined;

    if (formControls.title.dirty) {
      res.title = formValue.title;
    }
    if (formControls.locationIds.dirty) {
      res.locations = locations?.filter((value) => value);
    }
    if (formControls.name.dirty) {
      res.name = formValue.name;
    }
    if (formControls.phone.dirty) {
      res.phoneNumber = formValue.phone;
    }

    return res;
  }
  handleAction(type: UserAction) {
    switch (type) {
      case UserAction.EDIT:
        this.setEdit(true);
        break;
    }
    this.popoverService.setState(true);
  }
}
